import { useState } from "react";
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { formatLocalYmd } from "@heraion/shared";
import {
  LegalLinks,
  failureText,
  makeSettingsCardStyles,
  withdrawAgeFlow,
  type AccountActionOutcome,
  type UiTheme,
} from "@heraion/ui";
import { apiFetch } from "./api";
import { deleteAccount } from "./auth/deleteAccount";
import { confirmViaWindow } from "./confirm";
import { useShellProfile } from "./ThemeProfileBridge";

// Settings → Account's privacy card on the web console: export downloads a
// JSON file, the age declaration is withdrawn (#383), and delete runs the
// one web delete-account flow the age gate also runs.

export type PrivacyBusy = "export" | "withdraw" | "delete" | null;

export function PrivacyActions({
  theme,
  onSignedOut,
  busy,
  setBusy,
}: {
  theme: UiTheme;
  /** The flow signs out itself; this is the auth context's sign-out. */
  onSignedOut: () => void;
  /** Owned by the screen, so its sign-out waits too. */
  busy: PrivacyBusy;
  setBusy: (busy: PrivacyBusy) => void;
}) {
  const { t } = useTranslation();
  const card = makeSettingsCardStyles(theme);
  const errorStyle = makeStyles(theme).error;
  const { noteAgeWithdrawn, refresh } = useShellProfile();
  const [error, setError] = useState<string | null>(null);
  const busyAs = (kind: PrivacyBusy) => (on: boolean) => setBusy(on ? kind : null);
  const settle = (outcome: AccountActionOutcome, onDone: () => void) => {
    if (outcome === "done") onDone();
    else if (outcome !== "cancelled") setError(outcome.error);
  };

  const onExport = async () => {
    if (busy) return;
    setBusy("export");
    setError(null);
    try {
      const data = await apiFetch<unknown>("/api/me/export");
      const date = formatLocalYmd(new Date());
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `metria-export-${date}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(failureText(e, t, "mobile.account.exportNeedsConnection"));
    } finally {
      setBusy(null);
    }
  };

  // The gate comes back over the app as soon as the server took it; the
  // re-read then follows (it left after the gesture, so it decides).
  const onWithdrawAge = async () => {
    if (busy) return;
    setError(null);
    settle(
      await withdrawAgeFlow({ confirm: confirmViaWindow, apiFetch, t, onBusy: busyAs("withdraw") }),
      () => {
        noteAgeWithdrawn();
        void refresh();
      },
    );
  };

  // `done` needs nothing here: the flow's sign-out leaves the shell.
  const onDelete = async () => {
    if (busy) return;
    setError(null);
    settle(await deleteAccount({ t, signOut: onSignedOut, onBusy: busyAs("delete") }), () => {});
  };

  const action = (
    kind: Exclude<PrivacyBusy, null>,
    label: string,
    onPress: () => Promise<void>,
  ) => {
    const danger = kind === "delete";
    return (
      <Pressable
        accessibilityRole="button"
        disabled={busy !== null}
        onPress={() => void onPress()}
        style={({ pressed }) => [
          danger ? card.dangerAction : card.secondaryAction,
          busy !== null && card.disabled,
          pressed && (danger ? card.dangerActionPressed : card.secondaryActionPressed),
        ]}
      >
        {busy === kind ? (
          <ActivityIndicator color={danger ? theme.palette.danger : theme.palette.ink} />
        ) : (
          <Text style={danger ? card.dangerActionText : card.secondaryActionText}>{label}</Text>
        )}
      </Pressable>
    );
  };

  return (
    <View style={card.card}>
      <Text style={card.cardLabel}>
        {t("mobile.profile.labelPrivacy", { defaultValue: "PRIVACY" })}
      </Text>
      <Text style={card.cardHelp}>
        {t("mobile.profile.privacyHelp", {
          defaultValue:
            "Export everything as JSON, withdraw your age declaration, or delete the account permanently.",
        })}
      </Text>
      <LegalLinks theme={theme} />
      {error ? (
        <Text
          style={errorStyle}
          accessibilityRole="alert"
          accessibilityLiveRegion="polite"
          aria-live="polite"
        >
          {error}
        </Text>
      ) : null}
      {action(
        "export",
        t("mobile.profile.exportButton", { defaultValue: "Export my data" }),
        onExport,
      )}
      {action("withdraw", t("mobile.account.withdrawAge"), onWithdrawAge)}
      {action(
        "delete",
        t("mobile.profile.deleteButton", { defaultValue: "Delete my account" }),
        onDelete,
      )}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  return StyleSheet.create({
    error: { color: theme.palette.danger, fontSize: theme.typography.sizes.base },
  });
}
